import { useState, useEffect } from 'react'
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock'
import { appToast } from '../../../../../components/AppToast'
import { downloadUpdateColumnsTemplate, uploadUpdateColumnsFile } from '../../api/studentsApi'

const UPDATEABLE_FIELDS = [
  { value: 'RollNo', label: 'Roll No' },
  { value: 'SMobile', label: 'Mobile No' },
  { value: 'SEmail', label: 'Email' },
  { value: 'IDCardNo', label: 'ID Card No' },
  { value: 'Address_Line1', label: 'Address Line 1' },
  { value: 'Address_Line2', label: 'Address Line 2' },
  { value: 'City', label: 'City' },
  { value: 'State', label: 'State' },
  { value: 'PinCode', label: 'Pincode' },
  { value: 'Gender', label: 'Gender' },
  { value: 'Landline', label: 'Landline' },
  { value: 'PRNNumber', label: 'PRN Number' },
  { value: 'ABCRegistrationNumber', label: 'ABC Registration No' },
  { value: 'SaralId', label: 'Saral ID' },
]

export default function UpdateColumnsModal({ isOpen, onClose, students = [] }) {
  const [selectedColumn, setSelectedColumn] = useState('')
  const [uploadedFile, setUploadedFile] = useState(null)
  const [isImporting, setIsImporting] = useState(false)

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) {
      setSelectedColumn('')
      setUploadedFile(null)
    }
  }, [isOpen])

  const handleDownloadSample = async () => {
    if (!selectedColumn) {
      appToast.warning('Please select a column name first.')
      return
    }
    try {
      await downloadUpdateColumnsTemplate(selectedColumn)
      appToast.success(`Sample Excel template for "${selectedColumn}" downloaded.`)
    } catch (err) {
      appToast.error(err?.response?.data?.detail || 'Failed to download template.')
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadedFile(file)
      appToast.info(`File selected: ${file.name}`)
    }
  }

  const handleImport = async () => {
    if (!selectedColumn) {
      appToast.warning('Please select a column name to update.')
      return
    }
    if (!uploadedFile) {
      appToast.warning('Please upload the filled Excel file.')
      return
    }
    setIsImporting(true)
    try {
      const res = await uploadUpdateColumnsFile(uploadedFile, selectedColumn)
      appToast.success(res?.message || `Column "${selectedColumn}" updated successfully for ${res?.affected_count || 0} student(s).`)
      onClose()
    } catch (err) {
      appToast.error(err?.response?.data?.detail || 'Failed to import updated column file.')
    } finally {
      setIsImporting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] max-h-[700px] w-full max-w-[780px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Blue Banner Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between bg-primary px-8 py-5">
          <div>
            <h2 className="text-xl font-extrabold text-on-primary tracking-tight">Update Columns</h2>
            <p className="mt-0.5 text-sm text-on-primary/80">
              Bulk update student fields via Excel import
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-on-primary/15 px-3 py-1 text-xs font-bold text-on-primary">
              {students.length} record{students.length !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-on-primary/70 transition-colors hover:bg-on-primary/10 hover:text-on-primary"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>
        </div>

        {/* ── Notice Banner ──────────────────────────────────────────────── */}
        <div className="border-b border-outline-variant/15 bg-blue-50 px-8 py-4">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined mt-0.5 text-lg text-blue-600">info</span>
            <p className="text-sm leading-relaxed text-blue-800">
              Use this option to Update Selected Columns Excel Sheets in 4 simple steps.
              Please Make Sure that Select Columns from given Option and Download a sample file,
              fill details and upload the file. Please make sure that all Values are entered properly.
            </p>
          </div>
        </div>

        {/* ── Scrollable Body ────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-8 py-6">
          <div className="space-y-6">
            {/* ── Step 1 ──────────────────────────────────────────────────── */}
            <StepCard number={1} title="Select Column Name">
              <select
                value={selectedColumn}
                onChange={(e) => setSelectedColumn(e.target.value)}
                className="w-full rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3 text-sm font-medium text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">-- Select a column to update --</option>
                {UPDATEABLE_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </StepCard>

            {/* ── Step 2 ──────────────────────────────────────────────────── */}
            <StepCard number={2} title="Download Sample File">
              <p className="text-sm text-on-surface-variant mb-3">
                Download the sample file, fill in the data for the selected column, then upload it.
              </p>
              <button
                type="button"
                onClick={handleDownloadSample}
                disabled={!selectedColumn}
                className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-sm font-bold text-primary transition-colors hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span className="material-symbols-outlined text-base">download</span>
                UpdateColumns.xlsx
              </button>
            </StepCard>

            {/* ── Step 3 ──────────────────────────────────────────────────── */}
            <StepCard number={3} title="Enter copy/paste data">
              <div className="rounded-xl bg-surface-container-low/60 px-4 py-3 text-sm text-on-surface-variant leading-relaxed">
                <p>
                  Open the downloaded sample file in Excel or Google Sheets.
                  Enter the updated values in the column next to each Student Code.
                  Make sure all Student Codes match existing records and values are entered properly.
                </p>
              </div>
            </StepCard>

            {/* ── Step 4 ──────────────────────────────────────────────────── */}
            <StepCard number={4} title="Upload excel file">
              <div className="flex items-center gap-4">
                <label className="cursor-pointer inline-flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-5 py-2.5 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-high">
                  <span className="material-symbols-outlined text-base">upload_file</span>
                  Choose File
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {uploadedFile && (
                  <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700">
                    <span className="material-symbols-outlined text-base">check_circle</span>
                    {uploadedFile.name}
                    <button
                      type="button"
                      onClick={() => setUploadedFile(null)}
                      className="ml-1 rounded p-0.5 text-emerald-500 hover:bg-emerald-100"
                    >
                      <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                  </div>
                )}
              </div>
            </StepCard>
          </div>
        </div>

        {/* ── Footer Actions ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 px-8 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-6 py-2.5 text-sm font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={isImporting || !selectedColumn || !uploadedFile}
            className="rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-on-primary shadow-sm transition-all hover:shadow-md disabled:opacity-50"
          >
            {isImporting ? (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                Importing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base">upload</span>
                Import
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

function StepCard({ number, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-black text-on-primary">
        {number}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="mb-2 text-sm font-bold text-on-surface">{title}</h3>
        {children}
      </div>
    </div>
  )
}
